import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Column } from "react-table";
import UserApi from "../../api/UserApi";
import IconChangePassword from "../../Icons/Command/ChangeUserPassword";
import IconChangeStatus from "../../Icons/Command/ChangeUserStatus";
import IconEditUser from "../../Icons/Command/EditStatus";
import IconRemoveUser from "../../Icons/Command/RemoveUser";
import IconGenerateOtp from "../../Icons/Command/GenerateOtp";
import IconChangeRole from "../../Icons/Command/ChangeUserRole";
import IconAddUser from "../../Icons/TopCommandPanel/AddUser";
import { ENotificationType } from "../../Model/ENotificationType";
import { EUserRole, User } from "../../Model/User";
import { sendNotification } from "../../redux/actions/NotificationAction";
import { getUserCredential } from "../../redux/reducers/LocalUserReducer";
import { getNotifications } from "../../redux/reducers/NotificationReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import DateUtil from "../../Utility/DateUtil";
import MtiButton from "../Buttons/MtiButton";
import CommandPanel from "../CommandPanel/CommandPanel";
import CommandButton from "../CommandPanel/CommandPanelButton";
import CommonTable from "../Common/CommonTable";
import Instruction from "../Common/Instruction";
import Modal from "../Common/Modal";
import Breadcrumb from "../Header/Breadcrumb";
import Header from "../Header/Header";
import HeaderButton from "../Header/HeaderButton";
import LayoutContent from "../LayoutContent";
import ReportAndSupportNavSubMenu from "../Report/ReportAndSupportNavSubMenu";
import ChangePasswordPanel from "./ChangePasswordPanel";
import ChangeRolePanel from "./ChangeRolePanel";
import CreateNewUser from "./CreateNewUser";
import EditProfilePanel from "./EditProfilePanel";
import GenerateOtpPanel from "./GenerateOtpPanel";
import GenerateOtp from "./GenerateOtp";
import "./UserManagement.css";

const SELECT = "SELECT";
const CHANGE_STATUS = "CHANGE_STATUS";
const CHANGE_ROLE = "CHANGE_ROLE";
const EDIT_USER = "EDIT_USER";
const CHANGE_PASSWORD = "CHANGE_PASSWORD";
const REMOVE_USER = "REMOVE_USER";
const GENERATE_OTP = "GENERATE_OTP";

export default function UserManagement() {
  const notifications = useTypedSelector((state) => getNotifications(state));
  const dispatch = useDispatch();
  const [users, setUsers] = useState([] as User[]);
  const [selectedUsers, setSelectedUsers] = useState([] as User[]);
  const [filteredUsers, setFilteredUsers] = useState([] as User[]);
  const [commandPanelState, setCommandPanelState] = useState(SELECT);
  const [showRemoveUserWarning, setShowRemoveUserWarning] = useState(false);
  const [showGenerateOtp, setShowGenerateOtp] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showChangeStatus, setShowChangeStatus] = useState(false);
  const user = useTypedSelector((state) => getUserCredential(state));
  const [clearFilter, setClearFilter] = useState({
    action: () => {},
  });

  const userColumns: Column<User>[] = React.useMemo(
    () => [
      {
        Header: "Username",
        id: "Username",
        accessor: (row) => (row.userName ? row.userName : ""),
      },
      {
        Header: "Name",
        id: "Name",
        accessor: (row) => (row.name ? row.name : ""),
      },
      // {
      //   Header: "Surname",
      //   id: "Surname",
      //   accessor: (row) => (row.surname ? row.surname : ""),
      // },
      {
        Header: "Email",
        id: "Email",
        accessor: (row) => (row.userEmail ? row.userEmail : ""),
      },
      {
        Header: "Date Created",
        id: "Date Created",
        accessor: (row) =>
          row.dateCreated
            ? DateUtil.toUsFormatString(new Date(row.dateCreated))
            : "",
      },
      {
        Header: "Role",
        id: "Role",
        accessor: (row) => (row.userRole ? EUserRole[row.userRole] : ""),
      },
      {
        Header: "Status",
        id: "Status",
        accessor: (row) => (row.isActive ? "ACTIVE" : "INACTIVE"),
      },
    ],
    []
  );

  const onUsersUpdated = () => {
    UserApi.getUsers()
      .then((users) => {
        setUsers(users);
      })
      .finally(() => {
        setShowAddUser(false);
      });
  };

  const onToggleActive = () => {
    let newUser = { ...selectedUsers[0] };
    newUser.isActive = !selectedUsers[0].isActive;
    UserApi.updateUser(newUser).then((result) => {
      if (notifications.length === 0) {
        dispatch(
          sendNotification(ENotificationType.Success, "Change Status Success")
        );
        onUsersUpdated();
      }
      setShowChangeStatus(false);
    });
  };

  const onClick = (id: string) => {
    setCommandPanelState(id);
  };

  const removeUser = () => {
    UserApi.removeUser(selectedUsers[0]).then(() => {
      UserApi.getUsers().then((users) => {
        setUsers(users);
      });
    });
    setShowRemoveUserWarning(false);
  };

  useEffect(() => {
    UserApi.getUsers().then((users) => {
      setUsers(users);
    });
  }, []);

  useEffect(() => {
    if (commandPanelState !== SELECT) {
      setCommandPanelState(SELECT);
    }
  }, [selectedUsers]);

  const isAdmin = user?.role === EUserRole.Administrator;
  const isSupport = user?.role === EUserRole.Support;
  const isSelfSelected = selectedUsers[0]?.userName === user?.userName;
  const isSelectedAdmin =
    selectedUsers[0]?.userRole === EUserRole.Administrator;
  const isSelectedSupport = selectedUsers[0]?.userRole === EUserRole.Support;

  return (
    <LayoutContent>
      <LayoutContent.Header>
        <Header>
          <Breadcrumb
            items={[
              {
                title: "User Management",
                to: "/report-support/user-management",
                disabled: false,
              },
            ]}
          />
          <Header.RightSide>
            <HeaderButton
              id={"ADD_USER"}
              label="Create New User"
              icon={<IconAddUser />}
              onClick={() => setShowAddUser(true)}
            />
          </Header.RightSide>
        </Header>
      </LayoutContent.Header>
      <LayoutContent.Content>
        <CommonTable<User>
          setClearFilter={setClearFilter}
          setSelectedRows={setSelectedUsers}
          setFilteredRow={setFilteredUsers}
          data={users}
          columns={userColumns}
          sortingRules={[
            {
              id: "Username",
            },
          ]}
        />
        <Modal
          show={showRemoveUserWarning}
          onHide={() => setShowRemoveUserWarning(false)}
          style={{ paddingLeft: 24, paddingRight: 24 }}
        >
          <Modal.Header
            hideButtonEnabled={false}
            className="delete-user-header"
          >
            <Modal.Title
              title="Delete User"
              style={{ fontSize: 24, fontWeight: 400 }}
            />
          </Modal.Header>
          <Modal.Body>
            <div className="delete-user-message">
              <p>
                This account will be permanently deleted. Note that this action
                cannot be reversed.
              </p>
              <br />
              <p>Are you sure you want to delete this user?</p>
            </div>
          </Modal.Body>
          <Modal.Footer className="delete-user-footer">
            <MtiButton
              size="large"
              onClick={removeUser}
              style={{ marginRight: 10 }}
            >
              Confirm
            </MtiButton>
            <MtiButton
              size="large"
              variant="secondary"
              onClick={() => setShowRemoveUserWarning(false)}
              style={{
                width: 87,
                minWidth: 87,
              }}
            >
              Back
            </MtiButton>
          </Modal.Footer>
        </Modal>
        {showAddUser && (
          <Modal
            show={true}
            onHide={() => setShowAddUser(false)}
            hideOnBackdropClick={false}
            style={{
              width: "unset",
              padding: 0,
            }}
          >
            <Modal.Body>
              <CreateNewUser
                userNames={users.map((u) => u.userName)}
                onComplete={() => onUsersUpdated()}
              />
            </Modal.Body>
          </Modal>
        )}
        {showGenerateOtp && selectedUsers.length != 0 && (
          <Modal
            show={showGenerateOtp}
            hideOnBackdropClick={false}
            style={{
              width: "unset",
              padding: 0,
            }}
          >
            <Modal.Body>
              <GenerateOtp
                userName={selectedUsers[0]?.userName ?? "Unknown"}
                adminUsername={user?.userName ?? "Administrator"}
                onClose={(success) => {
                  if (success) {
                    UserApi.getUsers( null, selectedUsers[0]?.userName, null, null, null, null).then((users) => {
                      const lastUsers = users.filter((user) => user.userName === selectedUsers[0].userName);
                      setSelectedUsers(lastUsers);
                      setShowGenerateOtp(false);
                      setCommandPanelState(GENERATE_OTP);
                    });
                  }else {
                    setShowGenerateOtp(false);
                  }
                }}
              />
            </Modal.Body>
          </Modal>
        )}
        {showChangeStatus && (
          <Modal show={showChangeStatus} hideOnBackdropClick={false}>
            <Modal.Header title="Confirmation" hideButtonEnabled={false} />
            <Modal.Body style={{ textAlign: "left" }}>
              <p style={{ margin: 0 }}>{`Are you sure you want to ${
                selectedUsers[0].isActive ? "deactivate" : "activate"
              } this user?`}</p>
              <Instruction
                text={
                  selectedUsers[0].isActive
                    ? "If you click YES, the user will no longer be able to log into the software."
                    : "If you click YES, the user will be able to log into the software again."
                }
                variant="warning"
                style={{ marginTop: 30 }}
              />
            </Modal.Body>
            <Modal.Footer
              style={{
                display: "flex",
                flexDirection: "row-reverse",
                marginTop: 30,
              }}
            >
              <MtiButton size="large" onClick={onToggleActive}>
                YES
              </MtiButton>
              <MtiButton
                size="large"
                variant="secondary"
                onClick={() => setShowChangeStatus(false)}
                style={{
                  marginRight: 10,
                }}
              >
                NO
              </MtiButton>
            </Modal.Footer>
          </Modal>
        )}
      </LayoutContent.Content>
      <LayoutContent.SubMenu>
        <ReportAndSupportNavSubMenu />
      </LayoutContent.SubMenu>
      <LayoutContent.CommandPanel>
        {selectedUsers.length > 0 && (
          <>
            {commandPanelState === SELECT && (
              <CommandPanel>
                <CommandPanel.UsersInfoHeader users={selectedUsers} />
                <CommandPanel.HorizontalContent>
                  <CommandButton
                    id={CHANGE_STATUS}
                    icon={<IconChangeStatus />}
                    content={"Activate or Deactivate User"}
                    disabled={
                      selectedUsers.length !== 1 ||
                      isSelfSelected ||
                      (!isAdmin && !isSupport) ||
                      isSelectedAdmin ||
                      isSelectedSupport
                    }
                    onClick={(id) => setShowChangeStatus(true)}
                  />

                  <CommandButton
                    id={CHANGE_ROLE}
                    icon={<IconChangeRole />}
                    content="Change Role"
                    disabled={
                      selectedUsers.length !== 1 ||
                      (!isAdmin && !isSupport) ||
                      selectedUsers[0].userName === "Administrator"
                    }
                    onClick={(id) => onClick(id)}
                    showArrow={false}
                  />

                  <CommandButton
                    id={EDIT_USER}
                    icon={<IconEditUser />}
                    content="Edit Profile"
                    disabled={
                      selectedUsers.length !== 1 ||
                      (!isAdmin && !isSupport && !isSelfSelected)
                    }
                    onClick={(id) => onClick(id)}
                  />

                  {/* <CommandButton
                    id={CHANGE_PASSWORD}
                    icon={<IconChangePassword fill="#414042" />}
                    content="Change Password"
                    disabled={selectedUsers.length !== 1 || !isSelfSelected}
                    onClick={(id) => onClick(id)}
                  /> */}

                  <CommandButton
                    id={REMOVE_USER}
                    icon={<IconRemoveUser fill="#414042" />}
                    content="Delete User"
                    disabled={
                      selectedUsers.length !== 1 ||
                      isSelfSelected ||
                      (!isAdmin && !isSupport) ||
                      isSelectedAdmin || isSelectedSupport
                    }
                    onClick={(id) => setShowRemoveUserWarning(true)}
                  />

                  <CommandButton
                    id={GENERATE_OTP}
                    icon={<IconGenerateOtp fill="#414042" />}
                    content="Generate Otp"
                    disabled={
                      selectedUsers.length !== 1 ||
                      isSelfSelected ||
                      (!isAdmin && !isSupport)
                    }
                    onClick={(id) => setShowGenerateOtp(true)}
                  />
                </CommandPanel.HorizontalContent>
              </CommandPanel>
            )}

            {commandPanelState === CHANGE_PASSWORD && (
              <ChangePasswordPanel
                user={selectedUsers[0]}
                backBtn={() => {
                  onClick(SELECT);
                }}
              />
            )}
            {/* {commandPanelState === CHANGE_STATUS && (
              <ChangeStatusPanel
                user={selectedUsers[0]}
                onUserUpdated={onUsersUpdated}
                backBtn={() => {
                  onClick(SELECT);
                }}
              />
            )} */}
            {commandPanelState === EDIT_USER && (
              <EditProfilePanel
                user={selectedUsers[0]}
                onUserUpdated={onUsersUpdated}
                backBtn={() => {
                  onClick(SELECT);
                }}
              />
            )}
            {commandPanelState === CHANGE_ROLE && (
              <ChangeRolePanel
                user={selectedUsers[0]}
                onUserUpdated={onUsersUpdated}
                backBtn={() => {
                  onClick(SELECT);
                }}
              />
            )}
            {commandPanelState === GENERATE_OTP && (
              <GenerateOtpPanel
                user={selectedUsers[0]}
                backBtn={() => {
                  onClick(SELECT);
                }}
              />
            )}
          </>
        )}
      </LayoutContent.CommandPanel>
    </LayoutContent>
  );
}
